import type { AuthState } from './types';
import { safeSessionStorage } from '@shared/utils/safeStorage';
import { captureAuthError } from '@core/sentry/utils';
import { initFullStory } from '@core/analytics/fullstory';

const AUTH_RETURN_KEYS = ['authToken', 'user_id', 'email', 'auth_callback', 'afid', 'ps', 'd', 'st', 't', 'ft', 'evt', 'wl'];
const AUTH_RETURN_FALLBACK_KEY = 'dineflow_auth_return_fallback';
const AUTH_RETURN_FALLBACK_MAX_AGE_MS = 5 * 60 * 1000;

export interface AuthReturnPayload {
  authState: AuthState | null;
  bookingParams: BookingReturnParams | null;
  flowId: string | null;
}

interface StoredAuthReturnFallback extends AuthReturnPayload {
  savedAt: number;
}

export function isAuthCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.has('auth_callback')) return true;
  const authToken = params.get('authToken');
  if (authToken) return true;
  const token = params.get('token');
  return !!token && token.startsWith('eyJ');
}

export function parseAuthReturn(): AuthReturnPayload {
  const params = new URLSearchParams(window.location.search);
  const authToken = params.get('authToken');
  const legacyToken = params.get('token');
  const token = authToken || (legacyToken && legacyToken.startsWith('eyJ') ? legacyToken : null);
  const userId = params.get('user_id');
  const email = params.get('email');
  const flowId = params.get('afid');

  let authState: AuthState | null = null;

  if (token) {
    authState = {
      isAuthenticated: true,
      userId: userId || undefined,
      email: email || undefined,
      token,
    };
  }

  const bookingParams = extractBookingParams(params);

  return { authState, bookingParams, flowId };
}

export function saveAuthReturnFallback(payload: AuthReturnPayload): void {
  if (!payload.authState?.token) return;
  const stored: StoredAuthReturnFallback = { ...payload, savedAt: Date.now() };
  safeSessionStorage.setItem(AUTH_RETURN_FALLBACK_KEY, JSON.stringify(stored));
  if (!safeSessionStorage.getItem(AUTH_RETURN_FALLBACK_KEY)) {
    captureAuthError('auth.session_storage_unavailable', {
      flowId: payload.flowId,
    });
  }
}

export function loadAuthReturnFallback(): AuthReturnPayload | null {
  const raw = safeSessionStorage.getItem(AUTH_RETURN_FALLBACK_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredAuthReturnFallback & { authFlowId?: string | null };
    if (Date.now() - parsed.savedAt > AUTH_RETURN_FALLBACK_MAX_AGE_MS) {
      safeSessionStorage.removeItem(AUTH_RETURN_FALLBACK_KEY);
      return null;
    }
    // Sessions serialized before the authFlowId → flowId rename saved the
    // field as `authFlowId`. Fall back to that for one deploy window so
    // in-flight users don't lose correlation. Safe to remove after all
    // clients have refreshed (5 min fallback TTL + deploy rollout).
    const flowId = parsed.flowId ?? parsed.authFlowId ?? null;
    return { authState: parsed.authState, bookingParams: parsed.bookingParams, flowId };
  } catch {
    safeSessionStorage.removeItem(AUTH_RETURN_FALLBACK_KEY);
    return null;
  }
}

export function clearAuthReturnFallback(): void {
  safeSessionStorage.removeItem(AUTH_RETURN_FALLBACK_KEY);
}

/** Takes the login return's token and booking params out of the URL, then loads FullStory, which a URL carrying the token kept off. */
export function cleanAuthReturnUrl(): void {
  const cleanUrl = new URL(window.location.href);
  const legacyToken = cleanUrl.searchParams.get('token');
  const keys = [...AUTH_RETURN_KEYS];
  if (legacyToken && legacyToken.startsWith('eyJ')) keys.push('token');
  keys.forEach((key) => cleanUrl.searchParams.delete(key));
  window.history.replaceState({}, '', cleanUrl.toString());
  initFullStory(import.meta.env.VITE_FULLSTORY_ORG_ID);
}

export function handleAuthReturn(): AuthReturnPayload {
  const payload = parseAuthReturn();
  cleanAuthReturnUrl();
  return payload;
}

/**
 * Booking context params encoded in the return URL as fallback.
 */
export interface BookingReturnParams {
  partySize?: number;
  date?: string;
  serviceType?: string;
  time?: string;
  flowType?: string;
  eventSlug?: string;
  isWaitingList?: boolean;
}

function extractBookingParams(params: URLSearchParams): BookingReturnParams | null {
  const ps = params.get('ps');
  const d = params.get('d');
  const st = params.get('st');
  const t = params.get('t');
  const ft = params.get('ft');
  const evt = params.get('evt');
  const wl = params.get('wl');

  // If none of the booking params exist, return null
  if (!ps && !d && !st && !t && !ft && !evt && !wl) return null;

  return {
    partySize: ps ? Number(ps) : undefined,
    date: d || undefined,
    serviceType: st || undefined,
    time: t || undefined,
    flowType: ft || undefined,
    eventSlug: evt || undefined,
    isWaitingList: wl === '1',
  };
}
